'use client';

import React, { useCallback } from 'react';
import {
  User,
  Users,
  Tag,
  Link,
  Wrench,
  Notebook,
  Archive,
  Building2,
  Phone,
  CreditCard,
  X,
  FileText,
  ShieldCheck,
} from 'lucide-react';
import { useAppSelector, useAppDispatch } from '@/core/store/hooks';
import { userApi } from '@/api/features/users';
import { organizationsApi } from '@/api/features/organizations';
import { organizationsMembersApi } from '@/api/features/organizations/endpoints/members';
import { foldersApi } from '@/api/features/folders/endpoints/core';
import { templatesApi } from '@/api/features/templates';
import { billingAccountApi } from '@/api/features/billing';
import { adminExportsApi } from '@/api/features/admin/endpoints/exports';
import { isAdmin as checkIsAdmin } from '@/core/auth/utils/roleChecks';

interface SettingsOption {
  id: string;
  label: string;
  icon: React.ReactNode;
  adminOnly?: boolean;
}

interface SettingsSidebarProps {
  activeSection: string;
  onSectionChange: (section: string) => void;
  isOpen?: boolean;
  onClose?: () => void;
}

const SettingsSidebar: React.FC<SettingsSidebarProps> = ({
  activeSection,
  onSectionChange,
  isOpen = true,
  onClose
}) => {
  const dispatch = useAppDispatch();
  const userProfile = useAppSelector(state => state.userProfile.session);
  const isAdmin = checkIsAdmin(userProfile);
  const organizationId = userProfile?.organizationId;

  // Prefetch section data on hover for instant loading
  const handleSectionHover = useCallback((sectionId: string) => {
    // Prefetch the data for the hovered section
    switch (sectionId) {
      case 'profile':
        dispatch(userApi.endpoints.getUserProfile.initiate(undefined, { forceRefetch: false }));
        break;
      case 'business':
        if (organizationId) {
          dispatch(organizationsApi.endpoints.getOrganizationDetails.initiate(organizationId, { forceRefetch: false }));
        }
        break;
      case 'phone':
        if (organizationId) {
          dispatch(organizationsApi.endpoints.getOrganizationPhoneNumbers.initiate(organizationId, { forceRefetch: false }));
        }
        break;
      case 'tags':
        dispatch(foldersApi.endpoints.getOrganizationFolders.initiate(undefined, { forceRefetch: false }));
        break;
      case 'agency':
        if (isAdmin) {
          dispatch(organizationsMembersApi.endpoints.getOrganizationMembers.initiate(undefined, { forceRefetch: false }));
        }
        break;
      case 'subscription':
        if (isAdmin) {
          dispatch(billingAccountApi.endpoints.getBillingAccount.initiate(undefined, { forceRefetch: false }));
        }
        break;
      case 'archive':
        dispatch(adminExportsApi.endpoints.getExportLogs.initiate(undefined, { forceRefetch: false }));
        break;
      case 'templates':
        dispatch(templatesApi.endpoints.getOrganizationTemplates.initiate(undefined, { forceRefetch: false }));
        break;
    }
  }, [dispatch, isAdmin, organizationId]);

  // Regular user settings options
  const regularOptions: SettingsOption[] = [
    {
      id: 'profile',
      label: 'Profile & Display',
      icon: <User size={20} />
    },
    {
      id: 'business',
      label: 'Your Business',
      icon: <Building2 size={20} />
    },
    {
      id: 'tags',
      label: 'Tag Management',
      icon: <Tag size={20} />
    },
    {
      id: 'templates',
      label: 'Message Templates',
      icon: <FileText size={20} />
    },
    {
      id: 'logging',
      label: 'Message Logging',
      icon: <Notebook size={20} />
    },
    {
      id: 'archive',
      label: 'Archive & Download',
      icon: <Archive size={20} />
    }
  ];

  // Admin-only settings options
  const adminOptions: SettingsOption[] = [
    {
      id: 'agency',
      label: 'Agency Management',
      icon: <Users size={20} />,
      adminOnly: true
    },
    {
      id: 'phone',
      label: 'Phone Number',
      icon: <Phone size={20} />,
      adminOnly: true
    },
    {
      id: 'subscription',
      label: 'Subscription & Billing',
      icon: <CreditCard size={20} />,
      adminOnly: true
    },
    {
      id: 'integrations',
      label: 'Integrations',
      icon: <Link size={20} />,
      adminOnly: true
    },
    {
      id: 'tools',
      label: 'Tools',
      icon: <Wrench size={20} />,
      adminOnly: true
    },
    {
      id: 'audit-log',
      label: 'Audit Log',
      icon: <ShieldCheck size={20} />,
      adminOnly: true
    }
  ];

  const handleSectionChange = (section: string) => {
    onSectionChange(section);
    // Close sidebar on mobile after selection
    if (onClose && window.innerWidth < 1024) {
      onClose();
    }
  };

  return (
    <>
      {/* Mobile/Tablet Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div
        className={`
          ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          fixed lg:static
          inset-y-0 left-0
          w-80 lg:w-64 xl:w-80
          flex flex-col h-full
          bg-card-bg border-r border-border
          transition-all duration-300 ease-in-out
          z-50 lg:z-auto
          ${!isOpen ? 'lg:w-0 lg:overflow-hidden xl:w-0' : ''}
        `}
        role="navigation"
        aria-label="Settings navigation"
        data-testid="settings-sidebar"
      >
        {/* Settings Header */}
        <div className="container-padding py-6" data-testid="settings-sidebar-header">
          <div className="flex items-center justify-between">
            <h1 className="text-heading text-scalable-heading text-2xl">Settings</h1>
            {/* Close button for mobile/tablet */}
            {onClose && (
              <button
                onClick={onClose}
                className="lg:hidden p-2 rounded-lg hover:bg-hover text-text-secondary"
                aria-label="Close settings menu"
              >
                <X size={20} />
              </button>
            )}
          </div>
        </div>

        {/* Settings Navigation */}
        <div className="flex-1 overflow-y-auto py-4" data-tour-id="settings-tabs">
          {/* Regular Settings */}
          <ul className="space-y-1 px-3">
            {regularOptions.map(option => (
              <li key={option.id}>
                <button
                  className={`
                    nav-item
                    ${activeSection === option.id ? 'nav-item-active' : ''}
                  `}
                  data-testid={`settings-nav-${option.id}`}
                  data-tour-id={`settings-nav-${option.id}`}
                  onClick={() => handleSectionChange(option.id)}
                  onMouseEnter={() => handleSectionHover(option.id)}
                  onFocus={() => handleSectionHover(option.id)}
                  aria-current={activeSection === option.id ? 'page' : undefined}
                >
                  <span className={`mr-3 ${activeSection === option.id ? 'text-primary' : 'text-text-secondary'}`}>
                    {option.icon}
                  </span>
                  <span>
                    {option.label}
                  </span>
                </button>
              </li>
            ))}
          </ul>

          {/* Admin Settings - Only show if user is admin */}
          {isAdmin && (
            <div className="mt-8">
              <div className="px-6 py-2">
                <h3 className="text-caption text-scalable-caption font-medium uppercase tracking-wide">
                  Admin Settings
                </h3>
              </div>
              <ul className="space-y-1 px-3">
                {adminOptions.map(option => (
                  <li key={option.id}>
                    <button
                      className={`
                        nav-item
                        ${activeSection === option.id ? 'nav-item-active' : ''}
                      `}
                      data-testid={`settings-nav-${option.id}`}
                      data-tour-id={`settings-nav-${option.id}`}
                      onClick={() => handleSectionChange(option.id)}
                      onMouseEnter={() => handleSectionHover(option.id)}
                      onFocus={() => handleSectionHover(option.id)}
                      aria-current={activeSection === option.id ? 'page' : undefined}
                    >
                      <span className={`mr-3 ${activeSection === option.id ? 'text-primary' : 'text-text-secondary'}`}>
                        {option.icon}
                      </span>
                      <span>
                        {option.label}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Version Info */}
        <div className="px-6 py-4">
          <p className="text-xs text-text-secondary">
            Voley v1.0
          </p>
        </div>
      </div>
    </>
  );
};

export default SettingsSidebar;